import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { VitalSignsFormData, UnitPreference } from '../types/vitalSigns'
import { Patient } from '../types/patient'
import { calculateBMI } from '../utils/vitalSignsUtils'
import {
  calculateHeightPercentile,
  calculateWeightPercentile,
  calculateBMIPercentile,
  calculateAgeInMonths,
} from '../utils/normativeData'
import { useAuth } from '../contexts/AuthContext'
import './VitalSignsForm.css'

// Helper to handle NaN/empty values for optional number fields
const optionalNumber = (min?: number, max?: number) => {
  return z
    .union([z.number(), z.nan(), z.null(), z.undefined()])
    .transform((val) => {
      if (val === null || val === undefined || (typeof val === 'number' && isNaN(val))) {
        return undefined
      }
      return val as number
    })
    .pipe(
      z
        .number()
        .min(min ?? 0)
        .max(max ?? Infinity)
        .optional()
    )
}

const vitalSignsSchema = z.object({
  patientId: z.string().min(1, 'Patient is required'),
  consultationId: z.string().optional(),
  recordedAt: z.string().optional(),
  // Cardiovascular
  bloodPressureSystolic: optionalNumber(50, 300),
  bloodPressureDiastolic: optionalNumber(30, 200),
  heartRate: optionalNumber(30, 250),
  pulseRhythm: z
    .union([
      z.enum(['regular', 'irregular', 'irregularly-irregular', 'regularly-irregular', 'other']),
      z.literal(''),
      z.undefined(),
    ])
    .transform((val) => (val === '' ? undefined : val))
    .optional(),
  ejectionFraction: optionalNumber(0, 100),
  // Respiratory
  respiratoryRate: optionalNumber(5, 60),
  peakFlow: optionalNumber(0),
  oxygenSaturation: optionalNumber(0, 100),
  // Temperature
  temperature: optionalNumber(30, 45),
  temperatureUnit: z
    .union([z.enum(['celsius', 'fahrenheit']), z.literal(''), z.undefined()])
    .transform((val) => (val === '' ? undefined : val))
    .optional(),
  // Physical Measurements
  height: optionalNumber(0, 300),
  heightUnit: z.enum(['cm', 'in']).optional(),
  weight: optionalNumber(0, 500),
  weightUnit: z.enum(['kg', 'lb', 'stone']).optional(),
  abdominalCircumference: optionalNumber(0, 200),
  abdominalCircumferenceUnit: z.enum(['cm', 'in']).optional(),
  headCircumference: optionalNumber(0, 100),
  headCircumferenceUnit: z.enum(['cm', 'in']).optional(),
  // Lipid Panel
  cholesterol: optionalNumber(0, 20),
  ldl: optionalNumber(0, 20),
  hdl: optionalNumber(0, 20),
  // Pain Assessment
  painSeverity: optionalNumber(0, 10),
  painScale: z
    .union([z.enum(['nrs', 'faces']), z.literal(''), z.undefined()])
    .transform((val) => (val === '' ? undefined : val))
    .optional(),
  // Lifestyle & Social
  physicalExercise: z
    .union([
      z.enum(['not-recorded', 'sedentary', 'light', 'moderate', 'vigorous', 'other']),
      z.literal(''),
      z.undefined(),
    ])
    .transform((val) => (val === '' ? undefined : val))
    .optional(),
  substanceMisuse: z.string().optional(),
  // Notes
  notes: z.string().optional(),
})

interface VitalSignsFormProps {
  patientId: string
  consultationId?: string
  patient?: Patient
  initialData?: Partial<VitalSignsFormData>
  onSubmit: (data: VitalSignsFormData) => Promise<void>
  onCancel?: () => void
  compact?: boolean // For floating widget - shows compact form
  userPreference?: UnitPreference
}

export function VitalSignsForm({
  patientId,
  consultationId,
  patient,
  initialData,
  onSubmit,
  onCancel,
  compact = false,
  userPreference = 'metric',
}: VitalSignsFormProps) {
  const { user } = useAuth()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<VitalSignsFormData>({
    resolver: zodResolver(vitalSignsSchema),
    defaultValues: {
      patientId,
      consultationId,
      recordedAt: initialData?.recordedAt || new Date().toISOString(),
      recordedBy: initialData?.recordedBy || user?.id,
      recordedByName: initialData?.recordedByName || (user ? `${user.firstName} ${user.lastName}` : undefined),
      // Set default units based on user preference
      heightUnit: initialData?.heightUnit || (userPreference === 'imperial' ? 'in' : 'cm'),
      weightUnit: initialData?.weightUnit || (userPreference === 'imperial' ? 'lb' : 'kg'),
      temperatureUnit: initialData?.temperatureUnit || (userPreference === 'imperial' ? 'fahrenheit' : 'celsius'),
      abdominalCircumferenceUnit: initialData?.abdominalCircumferenceUnit || (userPreference === 'imperial' ? 'in' : 'cm'),
      headCircumferenceUnit: initialData?.headCircumferenceUnit || (userPreference === 'imperial' ? 'in' : 'cm'),
      ...initialData,
    },
  })

  // Watch values for real-time calculations
  const height = watch('height')
  const heightUnit = watch('heightUnit')
  const weight = watch('weight')
  const weightUnit = watch('weightUnit')

  // Calculate BMI in real-time
  useEffect(() => {
    if (height && weight && heightUnit && weightUnit) {
      // Convert to cm and kg for BMI calculation
      let heightCm = height
      let weightKg = weight

      if (heightUnit === 'in') {
        heightCm = height * 2.54
      }
      if (weightUnit === 'lb') {
        weightKg = weight * 0.453592
      } else if (weightUnit === 'stone') {
        weightKg = weight * 6.35029
      }

      calculateBMI(heightCm, weightKg)
      // BMI is calculated on the backend
    }
  }, [height, weight, heightUnit, weightUnit])

  const handleFormSubmit = async (data: VitalSignsFormData) => {
    setIsSubmitting(true)
    try {
      // Ensure recordedBy is set from current user if not already set
      if (!data.recordedBy && user?.id) {
        data.recordedBy = user.id
      }
      if (!data.recordedByName && user) {
        data.recordedByName = `${user.firstName} ${user.lastName}`
      }
      if (!data.recordedAt) {
        data.recordedAt = new Date().toISOString()
      }

      // Calculate percentiles if patient data available
      if (patient && patient.dateOfBirth && patient.gender && (data.height || data.weight)) {
        const ageMonths = calculateAgeInMonths(patient.dateOfBirth)

        // Convert to cm/kg for percentile calculations
        let heightCm = data.height
        let weightKg = data.weight

        if (data.height && data.heightUnit === 'in') {
          heightCm = data.height * 2.54
        }
        if (data.weight) {
          if (data.weightUnit === 'lb') {
            weightKg = data.weight * 0.453592
          } else if (data.weightUnit === 'stone') {
            weightKg = data.weight * 6.35029
          }
        }

        if (heightCm && ageMonths >= 0 && ageMonths <= 60) {
          const heightPercentile = calculateHeightPercentile(heightCm, patient.dateOfBirth, patient.gender)
          if (heightPercentile !== null) {
            data.heightPercentile = heightPercentile
          }
        }

        if (weightKg && ageMonths >= 0 && ageMonths <= 60) {
          const weightPercentile = calculateWeightPercentile(weightKg, patient.dateOfBirth, patient.gender)
          if (weightPercentile !== null) {
            data.weightPercentile = weightPercentile
          }

          // Calculate BMI percentile
          if (heightCm) {
            const bmi = calculateBMI(heightCm, weightKg)
            if (bmi) {
              const bmiPercentile = calculateBMIPercentile(bmi, patient.dateOfBirth, patient.gender)
              if (bmiPercentile !== null) {
                data.bmiPercentile = bmiPercentile
              }
            }
          }
        }
      }

      await onSubmit(data)
    } catch (error) {
      console.error('Error submitting vital signs:', error)
      alert('Failed to save vital signs')
    } finally {
      setIsSubmitting(false)
    }
  }

  // Calculate BMI for display
  const calculatedBMI = (() => {
    if (!height || !weight || !heightUnit || !weightUnit) return null
    let heightCm = height
    let weightKg = weight
    if (heightUnit === 'in') heightCm = height * 2.54
    if (weightUnit === 'lb') weightKg = weight * 0.453592
    else if (weightUnit === 'stone') weightKg = weight * 6.35029
    return calculateBMI(heightCm, weightKg)
  })()

  if (compact) {
    // Compact version for floating widget - essential fields only
    return (
      <form onSubmit={handleSubmit(handleFormSubmit)} className="vital-signs-form compact">
        <div className="vital-signs-section">
          <div className="form-row">
            <div className="form-group">
              <label>BP Systolic</label>
              <input type="number" {...register('bloodPressureSystolic', { valueAsNumber: true })} placeholder="120" />
            </div>
            <div className="form-group">
              <label>BP Diastolic</label>
              <input type="number" {...register('bloodPressureDiastolic', { valueAsNumber: true })} placeholder="80" />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Heart Rate (bpm)</label>
              <input type="number" {...register('heartRate', { valueAsNumber: true })} placeholder="72" />
            </div>
            <div className="form-group">
              <label>Respiratory Rate</label>
              <input type="number" {...register('respiratoryRate', { valueAsNumber: true })} placeholder="16" />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Temp (°{userPreference === 'imperial' ? 'F' : 'C'})</label>
              <input type="number" step="0.1" {...register('temperature', { valueAsNumber: true })} placeholder={userPreference === 'imperial' ? '98.6' : '37.0'} />
            </div>
            <div className="form-group">
              <label>O₂ Sat (%)</label>
              <input type="number" {...register('oxygenSaturation', { valueAsNumber: true })} placeholder="98" />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Weight ({userPreference === 'imperial' ? 'lb' : 'kg'})</label>
              <input type="number" step="0.1" {...register('weight', { valueAsNumber: true })} />
            </div>
            <div className="form-group">
              <label>Height ({userPreference === 'imperial' ? 'in' : 'cm'})</label>
              <input type="number" step="0.1" {...register('height', { valueAsNumber: true })} />
              <input type="hidden" {...register('heightUnit')} value={userPreference === 'imperial' ? 'in' : 'cm'} />
            </div>
          </div>
          <div className="form-row">
            {calculatedBMI && (
              <div className="form-group">
                <label>BMI</label>
                <input type="text" value={calculatedBMI.toFixed(1)} readOnly className="readonly" />
              </div>
            )}
          </div>
          <input type="hidden" {...register('weightUnit')} value={userPreference === 'imperial' ? 'lb' : 'kg'} />
          <input type="hidden" {...register('temperatureUnit')} value={userPreference === 'imperial' ? 'fahrenheit' : 'celsius'} />
          <div className="form-actions">
            <button type="submit" disabled={isSubmitting} className="btn-primary">
              {isSubmitting ? 'Saving...' : 'Save Vitals'}
            </button>
            {onCancel && (
              <button type="button" onClick={onCancel} className="btn-secondary">
                Cancel
              </button>
            )}
          </div>
        </div>
      </form>
    )
  }

  // Full form version
  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="vital-signs-form">
      {/* Section 1: BP, Pulse and Other */}
      <div className="vital-signs-section">
        <h3>BP, Pulse and Other</h3>
        <div className="form-row">
          <div className="form-group">
            <label>Systolic</label>
            <input type="number" {...register('bloodPressureSystolic', { valueAsNumber: true })} />
            {errors.bloodPressureSystolic && <span className="error-message">{errors.bloodPressureSystolic.message}</span>}
          </div>
          <div className="form-group">
            <label>Diastolic</label>
            <input type="number" {...register('bloodPressureDiastolic', { valueAsNumber: true })} />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Cholesterol (mmol/L)</label>
            <input type="number" step="0.1" {...register('cholesterol', { valueAsNumber: true })} />
          </div>
          <div className="form-group">
            <label>LDL (mmol/L)</label>
            <input type="number" step="0.1" {...register('ldl', { valueAsNumber: true })} />
          </div>
          <div className="form-group">
            <label>HDL (mmol/L)</label>
            <input type="number" step="0.1" {...register('hdl', { valueAsNumber: true })} />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Pulse (bpm)</label>
            <input type="number" {...register('heartRate', { valueAsNumber: true })} />
          </div>
          <div className="form-group">
            <label>Pulse Rhythm</label>
            <select {...register('pulseRhythm')}>
              <option value="">Select...</option>
              <option value="regular">Regular</option>
              <option value="irregular">Irregular</option>
              <option value="irregularly-irregular">Irregularly Irregular</option>
              <option value="regularly-irregular">Regularly Irregular</option>
              <option value="other">Other</option>
            </select>
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Temp ({userPreference === 'imperial' ? '°F' : '°C'})</label>
            <input type="number" step="0.1" {...register('temperature', { valueAsNumber: true })} />
            <input type="hidden" {...register('temperatureUnit')} value={userPreference === 'imperial' ? 'fahrenheit' : 'celsius'} />
          </div>
          <div className="form-group">
            <label>Peak Flow (L/min)</label>
            <input type="number" {...register('peakFlow', { valueAsNumber: true })} />
          </div>
          <div className="form-group">
            <label>Respiratory Rate (breaths/min)</label>
            <input type="number" {...register('respiratoryRate', { valueAsNumber: true })} />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Ejection Fraction (%)</label>
            <input type="number" {...register('ejectionFraction', { valueAsNumber: true })} />
          </div>
          <div className="form-group">
            <label>Physical Exercise</label>
            <select {...register('physicalExercise')}>
              <option value="not-recorded">Not Recorded</option>
              <option value="sedentary">Sedentary</option>
              <option value="light">Light</option>
              <option value="moderate">Moderate</option>
              <option value="vigorous">Vigorous</option>
              <option value="other">Other</option>
            </select>
          </div>
        </div>
        <div className="form-group">
          <label>Substance Misuse</label>
          <textarea {...register('substanceMisuse')} rows={3}></textarea>
        </div>
      </div>

      {/* Section 2: Physical Measurements */}
      <div className="vital-signs-section">
        <h3>Physical Measurements</h3>
        <div className="form-row">
          <div className="form-group">
            <label>Weight ({userPreference === 'imperial' ? 'lb' : 'kg'})</label>
            <input type="number" step="0.1" {...register('weight', { valueAsNumber: true })} />
            <input type="hidden" {...register('weightUnit')} value={userPreference === 'imperial' ? 'lb' : 'kg'} />
          </div>
          <div className="form-group">
            <label>Height ({userPreference === 'imperial' ? 'in' : 'cm'})</label>
            <input type="number" step="0.1" {...register('height', { valueAsNumber: true })} />
            <input type="hidden" {...register('heightUnit')} value={userPreference === 'imperial' ? 'in' : 'cm'} />
          </div>
          <div className="form-group">
            <label>BMI</label>
            <input type="text" value={calculatedBMI ? calculatedBMI.toFixed(1) : ''} readOnly className="readonly" />
          </div>
        </div>
        {calculatedBMI && patient && patient.dateOfBirth && patient.gender && calculateAgeInMonths(patient.dateOfBirth) <= 60 && (
          <div className="form-group">
            <span className="percentile-info">
              BMI Percentile: {(() => {
                let heightCm = height
                let weightKg = weight
                if (heightUnit === 'in') heightCm = (height || 0) * 2.54
                if (weightUnit === 'lb') weightKg = (weight || 0) * 0.453592
                else if (weightUnit === 'stone') weightKg = (weight || 0) * 6.35029
                if (heightCm && weightKg) {
                  const bmi = calculateBMI(heightCm, weightKg)
                  if (bmi) {
                    const pct = calculateBMIPercentile(bmi, patient.dateOfBirth, patient.gender)
                    return pct !== null ? `${pct}th` : 'N/A'
                  }
                }
                return 'N/A'
              })()}
            </span>
          </div>
        )}
        <div className="form-row">
          <div className="form-group">
            <label>Abdominal Circumference ({userPreference === 'imperial' ? 'in' : 'cm'})</label>
            <input type="number" step="0.1" {...register('abdominalCircumference', { valueAsNumber: true })} />
          </div>
          <div className="form-group">
            <label>Head Circumference ({userPreference === 'imperial' ? 'in' : 'cm'})</label>
            <input type="number" step="0.1" {...register('headCircumference', { valueAsNumber: true })} />
          </div>
        </div>
        <div className="form-group">
          <label>Pain Severity (0-10)</label>
          <input type="number" min="0" max="10" {...register('painSeverity', { valueAsNumber: true })} />
          <select {...register('painScale')} style={{ marginLeft: '8px' }}>
            <option value="nrs">NRS</option>
            <option value="faces">Faces</option>
          </select>
        </div>
      </div>

      <div className="form-group">
        <label>Notes</label>
        <textarea {...register('notes')} rows={3}></textarea>
      </div>

      <div className="form-actions">
        <button type="submit" disabled={isSubmitting} className="btn-primary">
          {isSubmitting ? 'Saving...' : 'Save Vitals'}
        </button>
        {onCancel && (
          <button type="button" onClick={onCancel} className="btn-secondary">
            Cancel
          </button>
        )}
      </div>
    </form>
  )
}
